import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EMPTY_FUNCTION,
  EMPTY_STRING,
  FLOATING_ELEMENT_PLACEMENT,
} from '../../../constants/common'
import { getQaSelector } from '../../../utils/common'
import {
  isRepeatedClick,
  isSubmitKeyDown,
  preventDefault,
} from '../../../utils/events'
import { AIButtonContainedDark, AIButtonContainedLight } from '../../AIButton'
import { FloatingTooltip } from '../../Floating'
import { TextareaElement, useTextareaFocusHandler } from '../../Textarea'

import {
  GRETA_COMPOSER_DOUBLE_CLICK_TIMEOUT_MS,
  GRETA_COMPOSER_MAX_LENGTH,
  GRETA_COMPOSER_MAX_ROWS,
  GRETA_COMPOSER_MIN_ROWS,
  QA_NAME_GRETA_AI_CHAT_COMPOSER,
  QA_NAME_GRETA_AI_CHAT_COMPOSER_INPUT,
  QA_NAME_GRETA_AI_CHAT_COMPOSER_SEND,
  QA_NAME_GRETA_AI_CHAT_COMPOSER_STOP,
} from './GretaAIChatComposer.constants'
import { getGretaAIChatComposerPlaceholderKey } from './GretaAIChatComposer.utils'

import * as styles from './GretaAIChatComposer.module.css'

import StopIcon from '../../../icons/stop.svg'

function GretaAIChatComposer({
  inputRef,
  isGenerating = false,
  isLimitReached = false,
  isLocked = false,
  isSendPending = false,
  value = EMPTY_STRING,
  onChange = EMPTY_FUNCTION,
  onSend = EMPTY_FUNCTION,
  onStop = EMPTY_FUNCTION,
}) {
  const { t } = useTranslation()

  const { focused, handleBlur, handleFocus } = useTextareaFocusHandler()
  const lastSendAtRef = useRef(0)

  const qaProps = getQaSelector({ value: QA_NAME_GRETA_AI_CHAT_COMPOSER })

  const isSendDisabled =
    isGenerating ||
    isLimitReached ||
    isLocked ||
    isSendPending ||
    value.trim().length === 0

  const placeholderKey = getGretaAIChatComposerPlaceholderKey({
    isLimitReached,
    isLocked,
  })

  function handleSend() {
    if (!isSendDisabled) {
      lastSendAtRef.current = Date.now()
      onSend(value)
    }
  }

  function handleStop(event) {
    // The second click of a double-click can land on Stop after Send rerenders.
    const shouldIgnoreStop =
      isRepeatedClick(event) ||
      Date.now() - lastSendAtRef.current <
        GRETA_COMPOSER_DOUBLE_CLICK_TIMEOUT_MS

    if (!shouldIgnoreStop) {
      onStop()
    }
  }

  function handleKeyDown(event) {
    if (isSubmitKeyDown(event)) {
      preventDefault(event)
      handleSend()
    }
  }

  const sendTooltip = isSendDisabled
    ? undefined
    : t('Greta.ComposerSendTooltipKey')

  return (
    <div className={styles.root}>
      <FloatingTooltip content={t(placeholderKey)} disabled={!isLocked}>
        <div {...qaProps} className={styles.composer}>
          <TextareaElement
            autoResizeHeight
            className={styles.input}
            disabled={isLimitReached || isLocked}
            focused={focused}
            hidePlaceholderOnFocus={false}
            maxLength={GRETA_COMPOSER_MAX_LENGTH}
            maxRows={GRETA_COMPOSER_MAX_ROWS}
            minRows={GRETA_COMPOSER_MIN_ROWS}
            placeholder={t(placeholderKey)}
            qaName={QA_NAME_GRETA_AI_CHAT_COMPOSER_INPUT}
            ref={inputRef}
            value={value}
            onBlur={handleBlur}
            onChange={onChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
          >
            <div className={styles.actions}>
              {isGenerating ? (
                <AIButtonContainedLight
                  beforeIconProps={{ Icon: StopIcon }}
                  qaName={QA_NAME_GRETA_AI_CHAT_COMPOSER_STOP}
                  text={t('Greta.StopButtonKey')}
                  tooltip={t('Greta.StopTooltipKey')}
                  tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.top}
                  onClick={handleStop}
                />
              ) : (
                <AIButtonContainedDark
                  disabled={isSendDisabled}
                  qaName={QA_NAME_GRETA_AI_CHAT_COMPOSER_SEND}
                  text={t('Greta.SendButtonKey')}
                  tooltip={sendTooltip}
                  tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.top}
                  onClick={handleSend}
                />
              )}
            </div>
          </TextareaElement>
        </div>
      </FloatingTooltip>
      <p className={styles.disclaimer}>{t('Greta.ComposerDisclaimerKey')}</p>
    </div>
  )
}

export default GretaAIChatComposer
